import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, UserButton } from "@clerk/clerk-react";
import { FiSend, FiBookOpen, FiMessageSquare, FiX, FiEdit3, FiSearch, FiFileText, FiRefreshCw, FiPlus } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import getBackendURL from "./getBackendURL";
import EnterCodeForm from "./EnterCode";

export default function Dashboard() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [codeRequired, setCodeRequired] = useState(null); // null = checking, true = show form, false = show main app
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const messagesEndRef = useRef(null);

  const [threadId, setThreadId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [loadingForThreadId, setLoadingForThreadId] = useState(null); // Track which conversation is loading
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const fetchConversations = useCallback(async () => {
    if (!isLoaded || !isSignedIn || !selectedCourse) return;
    setIsLoadingConversations(true);
    try {
      const token = await getToken({ template: "fastapi" });
      const response = await fetch(
        `${getBackendURL()}/conversations${selectedCourse ? `?course=${selectedCourse}` : ""}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      if (!response.ok) {
        throw new Error("Gespräche konnten nicht geladen werden.");
      }
      const data = await response.json();
      setConversations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading conversations:", err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [getToken, isLoaded, isSignedIn, selectedCourse]);

  const loadCourses = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    setIsLoadingCourses(true);
    try {
      const token = await getToken({ template: "fastapi" });
      const response = await fetch(`${getBackendURL()}/containers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        if (data.detail === "no_code") {
          setCodeRequired(true);
          setCourses([]);
          return;
        }
      }
      if (response.ok) {
        const courseList = await response.json();
        setCourses(courseList);
        if (courseList.length > 0) {
          setSelectedCourse(courseList[0]); // Auto-select first course
        }
        setCodeRequired(false);
      } else {
        setError("Kurse konnten nicht geladen werden.");
        setCodeRequired(false);
      }
    } catch (err) {
      setError("Kurse konnten nicht geladen werden.");
      console.error("Error loading courses:", err);
      setCodeRequired(false);
    } finally {
      setIsLoadingCourses(false);
    }
  }, [isLoaded, isSignedIn, getToken]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const startNewConversation = () => {
    setMessages([]);
    setThreadId(null);
    setSelectedConversationId(null);
    setError(null);
  };

  const loadConversationHistory = async (conversation) => {
    if (!conversation?.thread_id) return;
    setIsLoadingHistory(true);
    setError(null);

    try {
      const token = await getToken({ template: "fastapi" });
      const response = await fetch(
        `${getBackendURL()}/conversation_history/${conversation.thread_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Verlauf konnte nicht geladen werden.");
      }

      const history = await response.json();
      const formattedMessages = [];
      history.forEach((entry) => {
        if (entry.question) {
          formattedMessages.push({ type: "user", content: entry.question });
        }
        if (entry.answer) {
          formattedMessages.push({
            type: "bot",
            content: entry.answer,
            sources: entry.sources || [],
          });
        }
      });
      setMessages(formattedMessages);
      setThreadId(conversation.thread_id);
      setSelectedConversationId(conversation.thread_id);
    } catch (err) {
      setError("Verlauf konnte nicht geladen werden.");
      console.error("Error loading conversation history:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = { type: "user", content: inputValue.trim() };
    const currentThreadId = threadId; // Capture current thread ID before async operation
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);
    setLoadingForThreadId(currentThreadId); // Track which conversation is loading (null for new conversations)
    setError(null);

    try {
      const token = await getToken({ template: "fastapi" });

      const response = await fetch(`${getBackendURL()}/answer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          question: userMessage.content,
          course: selectedCourse,
          thread_id: threadId,
        }),
      });

      if (!response.ok) {
        throw new Error("Antwort konnte nicht geladen werden.");
      }

      const data = await response.json();
      console.log("data", data);

      // Show warning if session expired
      if (data.warning) {
        const warningMessage = {
          type: "warning",
          content: data.warning,
        };
        setMessages((prev) => [...prev, warningMessage]);
      }

      const botMessage = {
        type: "bot",
        content: data.answer,
        sources: data.sources || [],
      };
      setMessages((prev) => [...prev, botMessage]);
      setThreadId(data.thread_id);
      setSelectedConversationId(data.thread_id);
      fetchConversations();
    } catch (err) {
      setError("Antwort konnte nicht geladen werden. Bitte versuche es erneut.");
      console.error("Error:", err);
    } finally {
      setIsLoading(false);
      setLoadingForThreadId(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderConversationDate = (isoDate) => {
    if (!isoDate) return "";
    try {
      const date = new Date(isoDate);
      return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "";
    }
  };

  const submitFeedback = async () => {
    if (!feedbackText.trim()) return;

    setIsSubmittingFeedback(true);
    try {
      const response = await fetch(`${getBackendURL()}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          feedback: feedbackText.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error("Feedback konnte nicht gesendet werden.");
      }

      setFeedbackSubmitted(true);
      setFeedbackText("");
      setTimeout(() => {
        setIsFeedbackModalOpen(false);
        setFeedbackSubmitted(false);
      }, 2000);
    } catch (err) {
      setError("Feedback konnte nicht gesendet werden. Bitte versuche es erneut.");
      console.error("Error submitting feedback:", err);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 flex-shrink-0 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            {/* Logo */}
            <img src="/eddy_logo.png" alt="Eddy" className="w-10 h-10 rounded-xl flex-shrink-0" />
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Eddy</h1>
              <p className="text-sm text-gray-600">
                Dein Lernbegleiter
              </p>
            </div>
            <div className="flex items-center gap-3">
              {codeRequired !== true && (
                <button
                  onClick={() => setIsFeedbackModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all hover:scale-105"
                  title="Feedback geben"
                >
                  <FiMessageSquare className="w-4 h-4" />
                  <span>Feedback</span>
                </button>
              )}
              <UserButton afterSignOutUrl="/" />
            </div>
          </div>
        </div>
      </div>

      {/* Content: loading, enter-code form, or main app */}
      {codeRequired === null && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-600">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-3"></div>
            <p>Lade…</p>
          </div>
        </div>
      )}
      {codeRequired === true && (
        <EnterCodeForm
          onSuccess={() => {
            setCodeRequired(false);
            loadCourses();
          }}
        />
      )}
      {codeRequired === false && (
      <>
      {/* Main Content - Two Panel Layout */}
      <div className="flex-1 flex max-w-7xl mx-auto w-full overflow-hidden">
        {/* Left Panel - Conversations */}
        <div className="w-80 border-r border-gray-200 p-6 pb-8 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Gespräche
            </h2>
            <button
              onClick={startNewConversation}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-all shadow-sm hover:shadow-md hover:scale-105"
            >
              <FiPlus className="w-4 h-4" />
              <span>Neu</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
            {isLoadingConversations && (
              <div className="text-sm text-gray-500">Lade Gespräche...</div>
            )}
            {!isLoadingConversations && conversations.length === 0 && (
              <p className="text-sm text-gray-500">
                Noch keine Gespräche für diesen Kurs.
              </p>
            )}
            {conversations.map((conversation) => (
              <button
                key={conversation.thread_id}
                onClick={() => loadConversationHistory(conversation)}
                className={`w-full text-left p-3 rounded-xl transition-all border-l-2 ${
                  selectedConversationId === conversation.thread_id
                    ? "border-l-orange-500 border border-orange-500 bg-orange-50 shadow-sm"
                    : "border-l-transparent border border-transparent hover:bg-gray-50 hover:border-l-orange-300"
                }`}
              >
                <p className="text-sm font-medium text-gray-900 truncate">
                  {conversation.last_question || "Ohne Titel"}
                </p>
                <p className="text-[11px] text-gray-400 mt-1">
                  {renderConversationDate(conversation.last_message_at)}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Right Panel - Chat Interface */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 pb-32 min-h-0">
            <div className="space-y-6">
              {isLoadingHistory && (
                <div className="flex justify-center py-12">
                  <div className="flex items-center space-x-3 text-gray-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                    <span>Verlauf wird geladen...</span>
                  </div>
                </div>
              )}

              {!isLoadingHistory && messages.length === 0 && (
                <div className="flex flex-col items-center justify-center min-h-full py-12 px-4">
                  <img src="/eddy_logo.png" alt="Eddy" className="w-24 h-24 mb-6 drop-shadow-lg" />
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Hallo, ich bin Eddy!</h2>
                  <p className="text-gray-500 text-center mb-10 max-w-md text-lg">
                    Stelle eine Frage zu deinen Kursmaterialien
                  </p>
                  
                  {/* How it works - Icon based */}
                  <div className="w-full max-w-2xl mb-10">
                    <div className="grid grid-cols-4 gap-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-3">
                          <FiEdit3 className="w-6 h-6 text-orange-500" />
                        </div>
                        <p className="text-sm text-gray-600">Frage stellen</p>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-3">
                          <FiSearch className="w-6 h-6 text-orange-500" />
                        </div>
                        <p className="text-sm text-gray-600">Materialien durchsuchen</p>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-3">
                          <FiFileText className="w-6 h-6 text-orange-500" />
                        </div>
                        <p className="text-sm text-gray-600">Antwort mit Quellen</p>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-3">
                          <FiRefreshCw className="w-6 h-6 text-orange-500" />
                        </div>
                        <p className="text-sm text-gray-600">Rückfragen stellen</p>
                      </div>
                    </div>
                  </div>

                  {/* Example questions as chips */}
                  <div className="w-full max-w-2xl">
                    <p className="text-sm text-gray-400 text-center mb-4">Oder probiere eine Beispiel-Frage:</p>
                    <div className="flex flex-wrap justify-center gap-3">
                      <button
                        onClick={() => setInputValue("Wie bilde ich eine Summe in Excel?")}
                        className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-full hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-all shadow-sm hover:shadow hover:scale-105"
                      >
                        Wie bilde ich eine Summe?
                      </button>
                      <button
                        onClick={() => setInputValue("Wie formatiere ich eine Zelle als $?")}
                        className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-full hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-all shadow-sm hover:shadow hover:scale-105"
                      >
                        Wie formatiere ich eine Zelle als $?
                      </button>
                      <button
                        onClick={() => setInputValue("Wie erstelle ich ein Diagramm?")}
                        className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-full hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-all shadow-sm hover:shadow hover:scale-105"
                      >
                        Wie erstelle ich ein Diagramm?
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div key={index}>
                  {message.type === "warning" && (
                    <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-lg mb-4">
                      <p className="text-sm font-medium">⚠️ {message.content}</p>
                    </div>
                  )}
                  {message.type !== "warning" && (
                    <div
                      className={`flex message-animate ${
                        message.type === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div className="max-w-2xl">
                        <div className="flex-1">
                          <div
                            className={`px-4 py-3 ${
                              message.type === "user"
                                ? "bg-orange-500 text-white rounded-2xl rounded-tr-md"
                                : "bg-gray-100 text-gray-900 rounded-2xl rounded-tl-md"
                            }`}
                          >
                            {message.type === "user" ? (
                              <p className="whitespace-pre-wrap">{message.content}</p>
                            ) : (
                              <div className="prose prose-sm max-w-none prose-invert">
                                <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                                  {message.content}
                                </ReactMarkdown>
                              </div>
                            )}
                          </div>

                          {/* Sources Section */}
                          {message.type === "bot" &&
                            Array.isArray(message.sources) &&
                            message.sources.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm text-gray-600 mb-2">Quellen</p>
                                <div className="flex flex-wrap gap-2">
                                  {message.sources.map((source, sourceIndex) => {
                                    const pageNumbers =
                                      Array.isArray(source.page_numbers) && source.page_numbers.length > 0
                                        ? source.page_numbers.join(", ")
                                        : null;

                                    return (
                                      <span
                                        key={sourceIndex}
                                        className="inline-flex items-center gap-2 rounded-full bg-gray-100 border border-gray-200 px-3 py-1 text-xs text-gray-800"
                                      >
                                        <span className="font-medium">
                                          {source.section_title || "Unbenannte Quelle"}
                                        </span>
                                        {pageNumbers && (
                                          <span className="text-gray-500">| Seiten {pageNumbers}</span>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isLoading && loadingForThreadId === selectedConversationId && (
                <div className="flex justify-start message-animate">
                  <div className="bg-gray-100 text-gray-900 px-4 py-3 rounded-2xl rounded-tl-md">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-orange-400 rounded-full typing-dot"></div>
                      <div className="w-2 h-2 bg-orange-400 rounded-full typing-dot"></div>
                      <div className="w-2 h-2 bg-orange-400 rounded-full typing-dot"></div>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

        {/* Input - Floating at bottom */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <div className="h-8 bg-gradient-to-b from-transparent to-white"></div>
          <div className="bg-white pt-2 pb-6 px-6 pointer-events-auto">
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Stelle eine Frage zum Kurs..."
                  className="w-full px-5 py-4 pr-16 bg-white border border-gray-200 rounded-3xl shadow-lg focus:outline-none focus:border-transparent resize-none text-gray-900 placeholder-gray-500 transition-all input-glow"
                  rows="3"
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="absolute bottom-4 right-4 w-10 h-10 bg-orange-500 text-white rounded-full hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-white disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105"
                >
                  <FiSend className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {isFeedbackModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Feedback geben</h2>
              <button
                onClick={() => {
                  setIsFeedbackModalOpen(false);
                  setFeedbackText("");
                  setFeedbackSubmitted(false);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              {feedbackSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-lg font-medium text-gray-900">Vielen Dank für dein Feedback!</p>
                  <p className="text-sm text-gray-600 mt-2">Dein Feedback wurde erfolgreich übermittelt.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Wir freuen uns über dein Feedback! Teile uns mit, was wir verbessern können oder was dir besonders gut gefällt.
                  </p>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Dein Feedback..."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-gray-900 placeholder-gray-500"
                    rows="6"
                    disabled={isSubmittingFeedback}
                  />
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setIsFeedbackModalOpen(false);
                        setFeedbackText("");
                        setFeedbackSubmitted(false);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all hover:scale-105"
                      disabled={isSubmittingFeedback}
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={submitFeedback}
                      disabled={!feedbackText.trim() || isSubmittingFeedback}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-105 flex items-center gap-2"
                    >
                      {isSubmittingFeedback ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Wird gesendet...</span>
                        </>
                      ) : (
                        <>
                          <FiSend className="w-4 h-4" />
                          <span>Feedback senden</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

